/*
 ** Here we keep all the functions that are specifically used to handle
 ** the transcript.
 */
import { wordEndingErrorType } from '../constants/constants';
import { placementPhonemeEquals } from './amirabet';
import { getPhonemePlacementEnabled } from '../components/Reader/Speech/Speech';

var stringSimilarity = require('string-similarity');
var doubleMetaphone = require('double-metaphone');
var stemmer = require('stemmer');
let jsdiff = require('diff');

const defaultSkipWindow = 1;
let skipWindow = defaultSkipWindow;

//input word is stripped of all punctuation and special characters into a clean alpha onlh word, which is returned as the output
export function cleanWord(word) {
  if (!word) return;
  return (word.replace(/[.,\/#!$%\^&\*;:{}=\-_`'~()""?!]/g, "").toLowerCase());
}

/*
 ** takes a phrase, ignores the punctuation marks and returns an Array containing
 ** all the words in the phrase or an empty array [] if there's no phrase being passed.
 */
  export function cleanAndSplit(phrase, preserveWordsForDisplay = false) {
    if(!phrase) return [];

    let outPhrase = phrase ? phrase.replace(/-/g, "") : "";
    if(preserveWordsForDisplay){
      // remove characters at word boundries
      // \b always matches at a word boundary
      // \B matches only where there isn't a word boundary
      // https://stackoverflow.com/questions/7110382/remove-punctuation-from-beginning-and-end-of-words-in-a-string
      outPhrase = outPhrase.replace(/\b[.,\/#!$%\^&\*;:<>{}=\_`'~()""¿?¡!]+\B|\B[.,\/#!$%\^&\*;:<>{}=\_`'~()""¿?¡!]+\b/g, "");
    } else {
      outPhrase = outPhrase.replace(/[.,\/#!$%\^&\*;:<>{}=\_`'~()""¿?¡!]/g, "");
    }

    //Replace multiple spaces
    outPhrase = outPhrase.replace(/ +(?= )/g,'');
    outPhrase = outPhrase.toLowerCase().split(' ');

    //Replace leading and trailing '
    for (let i = 0; i < outPhrase.length; i++) {
      outPhrase[i] = outPhrase[i].replace(/(^')|('$)/g, "");
    }

    return outPhrase.filter((word) => {
      return word !== "";
    });
  }

function markPartAdded(part) {
  part.ignored = false;
  part.added = true;
  part.removed = false;
  return part;
}

function markPartRemoved(part) {
  part.ignored = false;
  part.added = false;
  part.removed = true;
  return part;
}

function markPartIgnored(part){
  part.ignored = true;
  part.added = false;
  part.removed = false;
  return part;
}

function markPartUnchanged(part) {
  part.added = false;
  part.removed = false;
  return part;
}

//Returns true if we find 3 words in a row
//TODO: parameterize the length of the run
export function isRepeatedRun(inString) {
  if (inString.length < 3) return false;

  let splitString = inString.split(' ');
  let prevWord = splitString[0];
  let runLength = 1; //
  for (let i = 1; i <= splitString.length - 1; i++) {
    if (prevWord == splitString[i]) {
      runLength++;
      if (runLength >= 3) return true;
    } else {
      runLength = 1;
    }
    prevWord = splitString[i];
  }
  return false;
}

export function fixFragmentedMatch(diff){
  let splitPart = '';

  //Look for a matching part later in the transcript that has more than 2 contiguous words
  for (var i = 0; i < diff.length; i++) {
    //Skip added parts
    if (diff[i].added){
      continue;
    }
    //Stop looking for parts when we hit the next remove
    if (diff[i].removed){
       continue; //TODO: see if this needs to change back to a break
    }
    //Just keep looking if our match is greater than 1
    if (diff[i].count > 1){
      continue;
    } else {
      //Need to overcome the behavior of diff - with repeated words, it maches the first one and considers
      // anything else 'added'. This is logical, but screws up our search for contigous blocks. If the
      // next block is 'added' and the last word of that block matches this word (remember, we only get
      // here if it's a single word match), then mark this block 'added' and concatenate this word to the
      // block after this.
      if ((diff[i + 1]) && (diff[i + 2] && !(diff[i + 2].removed || diff[i + 2].added))){
        splitPart = diff[i + 1].value.split(' ');
        // If the last word in the next block matches, we're going to shift this word to the beginning of
        // the block after that.
        if (splitPart[splitPart.length - 1] == diff[i].value) {
          diff[i + 2].value = diff[i].value + ' ' + diff[i + 2].value;
          diff[i + 2].count = diff[i + 2].count + 1;
          //Since we combined the words and they're a match, mark it as such and mark the current block
          // as added so we ignore it
          diff[i] = markPartIgnored(diff[i]);

          //if we combined a 'removed' block, need to take the last word away from it
          if (diff[i + 1].removed) {
            //Mark the current word as being removed
            diff[i] = markPartRemoved(diff[i]);

            //Remove the last item from the array (because we moved it to the next matching item)
            splitPart.pop();
            diff[i + 1].value = splitPart.join(' ').trim();
            diff[i + 1].count = diff[i + 1].count - 1;
          }

          i += 2; //Need to reset the index for the fallthrough
        }
      }
    }
  }
  return diff.filter((block)=>{return !block.ignored;});
}


//This method looks for the next block of 2 words in a row and (at this time) defragments
// single-word matches. We loop over the diff until we find a matching block of 2 or more
// words, then fall out of the loop and do cleanup.
export function findNextMatch(diff, diffIndex,minMatchWordsToSkip) {
  //if this is the last block, just return. This makes the code below easier to manage.
  if (diffIndex >= (diff.length - 1)) {
    return false;
  }

  let i = 0;
  //Look for a matching part later in the transcript that has more than 2 contiguous words
  for (i = diffIndex + 1; i < diff.length; i++) {
    //Skip added parts
    if (diff[i].added){
      continue;
    }
    //Skip removed parts (keep these two separate - not yet convinced this is right behavior)
    if (diff[i].removed){
        continue; //TODO: see if this needs to change back to a break
    }
    //Just keep looking if we hit a match of less than our window
    if (diff[i].count >= minMatchWordsToSkip){
      break;
    }
  }

  //If we fell off the end of the array, only found a matching block of length 1, or if the last element
  // was added or removed, what we're going to do is completely ignore anything downstream.
  // The theory here is that once we hit a big enough skip with no matching 2-word runs after it,
  // we don't want to proceed any further so processing anything else is waste.
  if (!diff[i] || (diff[i].count == 1) || (diff[i].added) || (diff[i].removed)) {
    for (i = diffIndex + 1; i < diff.length; i++) {
      if(diff[i].count < minMatchWordsToSkip){
        diff[i].added = false;
        diff[i].removed = true;
      }
    }
    return false;
  }
  return true;
}

//This method replaces anything that involves multiple words
//This is necessary because of the ASR's proclivity to combine certain words.
//This method is a place to put hard-coded special cases and any additional n-gram
//stuff we may come up with.
function replaceMultiWordOverrides(inString){
  return inString.replace("saltwater","salt water");
}

//returns the diff array given two strings
export function simpleDiff(expected,actual){
  let matchThreshold = 0.7;

  let expectedText = cleanAndSplit(expected).join(" ");
  let actualText = cleanAndSplit(actual).join(" ");

  // options for the diffWords method
  const options = {
    ignoreCase: true,
    customEquals: fuzzyWordEquals.bind(this, matchThreshold),
  }
  let diff = jsdiff.diffWords(expectedText, actualText, options);

  diff = fixFragmentedMatch(diff);

  return diff;
}

// returns a clean transcript (Array) based on inPhrase and transcript
export function scoreTranscript(inPhrase, transcript, currentWordIndex=0, matchThreshold = 0.7, minMatchWordsToSkip=2, matchWindow=-1, removeOutOfContext=false) {
  var cleanTranscript = [];
  let lastskip = 0;
  let splitPhrase = cleanAndSplit(inPhrase);

  // ignore the punctuation marks on the Phrase and Transcript
  let diffcleanPhrase = cleanAndSplit(inPhrase).join(" ");

  //If the caller has specified a match window, truncate the expected text so we don't match outside the window
  if(matchWindow>=0){
    diffcleanPhrase = diffcleanPhrase.split(" ").splice(0,currentWordIndex+matchWindow).join(" ");
  }

  let diffcleanTranscript = cleanAndSplit(transcript.interim).join(" ");

  //Take care of any multi-word replacements (see definition of the method)
  diffcleanTranscript = replaceMultiWordOverrides(diffcleanTranscript);

  if(removeOutOfContext){
    diffcleanTranscript = diffcleanTranscript.split(" ").filter(word => fuzzyContains(diffcleanPhrase.split(" "), word, matchThreshold)).join(" ");
  }

  // options for the diffWords method
  const options = {
    ignoreCase: true,
    customEquals: fuzzyWordEquals.bind(this, matchThreshold),
  }
  let diff = jsdiff.diffWords(diffcleanPhrase, diffcleanTranscript, options);
  let index = 0;
  let phraseWords = diffcleanPhrase.split(' ');

  let totalErrors = [];
  let skips = [];
  let substitutions = [];
  let confidence = Array(currentWordIndex + 1).fill({
    stemmed: 0,
    stemmedMetaphone: 0,
    unprocessed: 0,
  });

  //Clean up fragmented matches before we iterate.
  // This should prevent us from matching on the wrong word
  // if it appears multiple times in the phrase.
  diff = fixFragmentedMatch(diff);
  diff.forEach((part, diffIndex) => {
    let status = part.added ? 'added' : part.removed ? 'removed' : 'unchanged';

    // only jump ahead if we have at least minMatchWordToSkip words in a row
    if (part.removed && part.count > skipWindow) {
      //If we don't find any aligned text, just return
      //No other processing is necessary - the find match cleans everything up.
      if (findNextMatch(diff, diffIndex,minMatchWordsToSkip) == false) return;
    }

    //At this point, we've found something interesting to do. Update the clean transcript accordingly.
    let diffWords = part.value.split(' ');

    let nextPart = diff[diffIndex + 1] || {};
    let nextPartWords = (nextPart.value && nextPart.value.split(' ')) || [];

    diffWords.forEach((word, wordIndex) => {
      switch (status) {
        case 'unchanged':
          if (word && (word != ' ')){
            cleanTranscript[index] = word;
            totalErrors[index] = false;
            skips[index] = false;
            substitutions[index] = false;
            let phraseWord = phraseWords[index];
            if(word && phraseWord){
              confidence[index] = {
                stemmed: calculateStemmedSimilarity(word, phraseWord),
                stemmedMetaphone: calculateStemmedMetaphoneSimilarity(word, phraseWord),
                unprocessed: (word && phraseWord) ? stringSimilarity.compareTwoStrings(word.toString(), phraseWord.toString()) : 0,
              };
            }else{
              confidence[index] = {
                stemmed: 0,
                stemmedMetaphone: 0,
                unprocessed: 0
              }
            }
            index ++;
          };
          break;
        case 'removed':
          //if we have actually omitted (or missed) a word of valid length, advance the index and leave a hole in the transcript
          if (word && (word != ' ')) {
            totalErrors[index] = true;

            let isSubstitution = (!!nextPart.added) && (nextPartWords.length > wordIndex);

            skips[index] = !isSubstitution;
            substitutions[index] = isSubstitution;
            index++;
          }
          lastskip = part.count;
          break;
        case 'added':
        default:
          break;
      }
    });
  });

  // prevent the part of the phrase the reader has not got to yet from being marked as a "skip"
  totalErrors = totalErrors.slice(0, cleanTranscript.length);
  skips = skips.slice(0, cleanTranscript.length);
  substitutions = substitutions.slice(0, cleanTranscript.length);

  //Leave this commented out but in the code
  //until we get more analog information about the certainty of the match.
  /* if(recordedErrors){
     let transcriptLength = Math.max(cleanTranscript.length, recordedErrors.length);
     let adjustedCleanTranscript = [];
     let targetPhrase = diffcleanPhrase.split(' ');
     for(let i = 0; i < transcriptLength; i++){
       //console.log("word: ", targetPhrase[i], ", recorded: ", cleanTranscript[i], ", previous error: ", recordedErrors[i]);
       if(cleanTranscript[i]){
         // pushing cleanTranscript[i] beacuse it was right this time
         adjustedCleanTranscript.push(cleanTranscript[i]);
       } else if (recordedErrors[i]){
         //recording an error because it was wrong this time and was wrong previously as well
         adjustedCleanTranscript.push(null);
       } else if(i <= recordedErrors.length){
         //pushing targetPhrase[i] because this word was previously marked right even though it was wrong this time through
         adjustedCleanTranscript.push(targetPhrase[i]);
       } else {
         adjustedCleanTranscript.push(null);
       }
     }
     return(adjustedCleanTranscript);
   }else{
     //console.log(cleanTranscript);
     return(cleanTranscript);
   }*/

  return ({
    cleanTranscript: cleanTranscript,
    totalErrors: totalErrors,
    skips: skips,
    substitutions: substitutions,
    confidence: confidence,
  });
}

//This function returns an object that contains two attributes:
// 1. skipLength: the length in words of the last block of text that was skipped
// 2. lastMatchLength: the length in words of the last contiguous run of words that was matched after a skip
export function calculateSkipMetrics(inTranscript){
  let index = inTranscript.length-1;
  let skipLength = 0;
  let lastMatchLength = 0;

  //Look backward through the string.
  for(index;index >=0;index--){
    //Terminate the loop when we find the next match after a skip
    if(inTranscript[index] && skipLength>0){
      break;
    }
    //Increment the last match as long as we have a word in the transcript
    if(inTranscript[index]){
      lastMatchLength++;
      continue;
    }
    //If we're here, we don't have a match. Increment the skip length
    skipLength++;
  }
  return {skipLength: skipLength, matchLength: lastMatchLength};
}

//Check for strange overrides that are needed because of ASR oddities
// returns true if there is a match and false if not.
function matchOverride(spokenWord,expectedWord){
  if((spokenWord === 'nick') && (expectedWord == 'mick')) return true;
  if((expectedWord == 'while') && (spokenWord == 'wow')) return true;
  if((expectedWord == 'asked') && (spokenWord == 'ass')) return true;
  if((expectedWord == 'six') && (spokenWord == "6")) return true;
  if((expectedWord == 'then') && ((spokenWord == "10") || (spokenWord == "ten"))) return true;
  if((expectedWord == 'five') && (spokenWord == "5")) return true;
  if((expectedWord == 'thirty') && (spokenWord == "30")) return true;
  if(((expectedWord == 'for') || (expectedWord == "four")) && (spokenWord == "4")) return true;

  return false
}

function fuzzyContains(words, word, matchThreshold){
  for(let i = 0; i < words.length; i++){
    if(fuzzyWordEquals(matchThreshold, word, words[i])){
      return true;
    }
  }
  return false;
}

function fuzzyContainsCount(words, word, matchThreshold){
  let matchCount = 0;
  for(let i = 0; i < words.length; i++){
    if(fuzzyWordEquals(matchThreshold, word, words[i])){
      matchCount++;
    }
  }
  return matchCount;
}

// helper functions for fuzzy match. Abstracted out so that we can pass this scoring byproduct to the back end to help with EDM
function calculateStemmedSimilarity(spokenWord, expectedWord){
  let stemmed1 = stemmer(spokenWord);
  let stemmed2 = stemmer(expectedWord);
  return (stemmed1 && stemmed2) ? stringSimilarity.compareTwoStrings(stemmed1.toString(), stemmed2.toString()) : 0;
}

function calculateStemmedMetaphoneSimilarity(spokenWord, expectedWord){
  let stemmedMetaphone1 = doubleMetaphone(stemmer(spokenWord))[0];
  let stemmedMetaphone2 = doubleMetaphone(stemmer(expectedWord))[0];
  return (stemmedMetaphone1 && stemmedMetaphone2) ? stringSimilarity.compareTwoStrings(stemmedMetaphone1.toString(), stemmedMetaphone2.toString()) : 0;
}

/*
 ** We use stringSimilarity to compare the two words and we use these 4 methods
 ** - unprocessed words, just play stringSimilarity comparison
 ** - using the stemmed words
 ** - doubleMetaphone with stemmed words
 ** - doubleMetaphone with unaltered words (spokenWord, expectedWord)
 **
 ** Returns true if at least one of our matching algorithms finds a match between the two words.
 **
 ** Note: The Order of the words does not matter
 */
export function fuzzyWordEquals(matchThreshold, spokenWord, expectedWord) {
  if(spokenWord === 'unk') return false;

  if (spokenWord === expectedWord) {
    return true;
  }

  if(matchOverride(spokenWord,expectedWord)){
    return true;
  }

  // PHONEME MATCHING LOGIC -- this will only run if phoneme placement is enabled
  if(getPhonemePlacementEnabled() && placementPhonemeEquals(spokenWord, expectedWord)){
    return true;
  }

  const similarity_threshold = matchThreshold;

  if (!!spokenWord && !!expectedWord) {
    // run four different comparisons -- stemmed metaphone, metaphone, stemmed, and un processed
    // if ANY of these find a match, count as a match, otherwise count as an error
    // NOTE: the multiple returns are, of course, horrible violations of good coding practice. This was done for performance reasons

    //unprocessed
    let unprocessedSimilarity = stringSimilarity.compareTwoStrings(spokenWord.toString(), expectedWord.toString());
    if (unprocessedSimilarity > similarity_threshold) return true;

    //stemmed
    let stemmedSimilarity = calculateStemmedSimilarity(spokenWord, expectedWord);
    if (stemmedSimilarity > similarity_threshold) return true;

    //stemmed metaphone
    let stemmedMetaphoneSimilarity = calculateStemmedMetaphoneSimilarity(spokenWord, expectedWord);
    if (stemmedMetaphoneSimilarity > similarity_threshold) return true;

    //Leave this here in case we believe we need it. The current hypothesis is that the stemmed metaphone converges to this case
/*    let metaphone1 = doubleMetaphone(spokenWord)[0];
    let metaphone2 = doubleMetaphone(expectedWord)[0];
    let metaphoneSimilarity = stringSimilarity.compareTwoStrings(metaphone1, metaphone2);
    if (metaphoneSimilarity > similarity_threshold) return true;*/

  }
  // fall through to return false if none of the conditions were met
  return false;
}

export function getSkipWindow(){
  return skipWindow;
}

export function setSkipWindow(newWindow){
  if(newWindow === 'default'){
    skipWindow = defaultSkipWindow;
  } else {
    skipWindow = newWindow;
  }

}

//Get possible endings:
// ING
// ED
// ES
// S
function filterWordEnding(ending) {
  if(ending.slice(-3) == 'ing') {
    return 'ing';
  }else if(ending.slice(-2) == 'ed') {
    return 'ed';
  }else if(ending.slice(-2) == 'es') {
    return 'es';
  }else if(ending.slice(-1) == 's') {
    return 's';
  }

  return null;
}

export function getWordEndingError(utteredWord, desiredWord){
  let utteredWordEnding;
  let desiredWordEnding;

  for(let i = 0; i < desiredWord.length; i++) {
    if(desiredWord[i] != utteredWord[i]){
      utteredWordEnding = utteredWord[i] ? utteredWord.slice(i - 1) : '';
      desiredWordEnding = desiredWord[i] ? desiredWord.slice(i - 1) : '';
      break;
    }
  }

  if(!utteredWordEnding && !desiredWordEnding){//It means desiredWord is shorter than utteredWord
    utteredWordEnding = filterWordEnding(utteredWord.slice(desiredWord.length - 1));
  }else {
    utteredWordEnding = utteredWordEnding ? filterWordEnding(utteredWordEnding.toLowerCase()) : null;
    desiredWordEnding = desiredWordEnding ? filterWordEnding(desiredWordEnding.toLowerCase()) : null;
  }

  if(utteredWordEnding && !desiredWordEnding) {//Added Letters Case
    return {
      type: wordEndingErrorType.ADDED,
      ending: utteredWordEnding,
    };
  }else if (!utteredWordEnding && desiredWordEnding) {//Dropped Letters Case
    return {
      type: wordEndingErrorType.DROPPED,
      ending: desiredWordEnding,
    };
  }else if (utteredWordEnding && desiredWordEnding) {//Substitution Case
    return {
      type: wordEndingErrorType.MIX,
      ending: desiredWordEnding,
    };
  }

  //TODO: Come up with a solution for special cases
  return {};
}

export function checkForResponseMatch(targetWord = '', transcript){
  const moveOnResponses = [ //TODO: find a better place for this const
    "skip",
    "don't know",
    "go on",
  ];

  targetWord = targetWord.toLowerCase();
  transcript = transcript.toLowerCase();

  return {
    targetMatches: NLP.fuzzyContainsCount(transcript.split(' '), targetWord, 1),
    moveOn: NLP.containsAny(transcript, moveOnResponses),
  }
}

export function containsAny(string, substrings){
  string = string.toLowerCase();
  return substrings.some(substring => string.includes(substring.toLowerCase()));
}

export function checkForMorphemeMatch(morpheme, morphemePosition, transcript){
  const words = transcript.split(' ').map(word => word.trim());
  return words.filter(word => {
    return (word.startsWith(morpheme) && morphemePosition === 'prefix')
        || (word.endsWith(morpheme) && morphemePosition === 'suffix')
        || (word.includes(morpheme) && morphemePosition === 'root'); //TODO: make root matching less fuzzy
  });
}

export const NLP = {
  fuzzyContainsCount: fuzzyContainsCount,
  containsAny: containsAny,
  checkForResponseMatch: checkForResponseMatch,
  checkForMorphemeMatch: checkForMorphemeMatch,
}
